import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import type { Product } from "@/types";
import { Badge, Price, StarsRating, ProductImage } from "@/components/atoms";
import { useCartStore } from "@/stores/cart-store";
import {
  defaultVariant,
  variantPrice,
  variantRegularPrice,
  primaryImage,
  isOnSale,
} from "@/lib/product-helpers";
import { track } from "@/lib/analytics";

// Vertical product card (~240×460). Used in bestsellers, category grids, shop, related rows.
export function ProductCard({ product }: { product: Product }) {
  const add = useCartStore((s) => s.add);
  const v = defaultVariant(product);
  const price = variantPrice(product, v);
  const regular = isOnSale(product)
    ? variantRegularPrice(product, v)
    : undefined;

  function quickAdd(e: React.MouseEvent) {
    e.preventDefault();
    add({
      productId: product.id,
      variantId: v.id,
      productName: product.name,
      variantLabel: v.label,
      slug: product.slug,
      unitPrice: price,
      quantity: 1,
      isSubscription: false,
      imageUrl: primaryImage(product),
    });
    track("add_to_cart", { item_id: product.id, value: price / 100 });
  }

  return (
    <Link
      to={`/products/${product.slug}`}
      className="group flex flex-col overflow-hidden rounded-card border border-night-blue/10 bg-white transition-shadow hover:shadow-lg"
    >
      <div className="relative aspect-square bg-medical-grey">
        {product.badge && (
          <div className="absolute left-3 top-3 z-10">
            <Badge kind={product.badge} accent={product.badgeAccent} />
          </div>
        )}
        <ProductImage
          src={primaryImage(product)}
          alt={product.name}
          name={product.name}
          className="h-full w-full"
          imgClassName="p-4 transition-transform group-hover:scale-105"
        />
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        {product.condition && (
          <span className="font-condensed text-[11px] font-bold uppercase tracking-wider text-healthy-blue">
            {product.condition}
          </span>
        )}
        <h3 className="font-condensed text-lg font-bold leading-tight text-night-blue">
          {product.name}
        </h3>
        <p className="line-clamp-2 text-sm text-night-blue/70">
          {product.shortDescription}
        </p>
        <StarsRating
          value={product.ratingsAverage}
          count={product.ratingsCount}
          size={14}
        />
        <div className="mt-auto flex items-center justify-between pt-2">
          <Price cents={price} regularCents={regular} />
          <button
            type="button"
            onClick={quickAdd}
            aria-label={`Add ${product.name} to cart`}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-calm-orange-ink text-white transition-transform active:scale-90 hover:bg-calm-orange-ink/90"
          >
            <ShoppingBag size={18} />
          </button>
        </div>
      </div>
    </Link>
  );
}
